import { Handle, Position, useReactFlow, type NodeProps } from "@xyflow/react";
import { useState, useCallback, useEffect, useRef } from "react";
import { textColorForBg, type FlowchartNodeData } from "@/lib/mermaid-reactflow-converter";

function EditableLabel({
    label,
    onChange,
}: {
    label: string;
    onChange: (value: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(label);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setValue(label);
    }, [label]);

    useEffect(() => {
        if (editing) inputRef.current?.select();
    }, [editing]);

    const commit = useCallback(() => {
        setEditing(false);
        if (value.trim() && value !== label) onChange(value.trim());
        else setValue(label);
    }, [value, label, onChange]);

    if (editing) {
        return (
            <input
                ref={inputRef}
                className="nodrag nopan bg-transparent text-center text-sm outline-none border-b border-primary w-full"
                value={value}
                autoFocus
                onChange={(e) => setValue(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") {
                        setValue(label);
                        setEditing(false);
                    }
                }}
            />
        );
    }
    return (
        <span
            className="text-sm select-none cursor-text truncate max-w-full"
            onDoubleClick={() => setEditing(true)}
            title="Double-click to edit"
        >
            {label}
        </span>
    );
}

function useUpdateNodeLabel(nodeId: string) {
    const { setNodes } = useReactFlow();
    return useCallback(
        (newLabel: string) => {
            setNodes((nds) =>
                nds.map((n) =>
                    n.id === nodeId ? { ...n, data: { ...n.data, label: newLabel } } : n,
                ),
            );
        },
        [nodeId, setNodes],
    );
}

function SourceTargetHandles() {
    return (
        <>
            <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-primary" />
            <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !bg-primary" />
        </>
    );
}

export function ProcessNode({ id, data, selected }: NodeProps) {
    const d = data as unknown as FlowchartNodeData;
    const updateLabel = useUpdateNodeLabel(id);
    const colorStyle = d.color
        ? { backgroundColor: d.color, color: textColorForBg(d.color) }
        : undefined;
    return (
        <div
            className={`px-4 py-2 rounded border min-w-[100px] text-center
                ${!d.color ? "bg-card text-card-foreground" : ""}
                ${selected ? "ring-2 ring-primary" : "border-border"}`}
            style={colorStyle}
        >
            <SourceTargetHandles />
            <EditableLabel label={d.label} onChange={updateLabel} />
        </div>
    );
}

export function DecisionNode({ id, data, selected }: NodeProps) {
    const d = data as unknown as FlowchartNodeData;
    const updateLabel = useUpdateNodeLabel(id);
    const colorStyle = d.color
        ? { backgroundColor: d.color }
        : undefined;
    return (
        <div
            className={`relative w-[120px] h-[120px] flex items-center justify-center
                ${selected ? "drop-shadow-[0_0_4px_rgb(var(--primary))]" : ""}`}
        >
            <div
                className={`absolute inset-[15%] rotate-45 border
                    ${!d.color ? "bg-card" : ""}
                    ${selected ? "ring-2 ring-primary" : "border-border"}`}
                style={colorStyle}
            />
            <div
                className={`relative z-10 px-1 text-center max-w-[80px]
                    ${!d.color ? "text-card-foreground" : ""}`}
                style={d.color ? { color: textColorForBg(d.color) } : undefined}
            >
                <EditableLabel label={d.label} onChange={updateLabel} />
            </div>
            <Handle
                type="target"
                position={Position.Top}
                className="!w-2 !h-2 !bg-primary"
                style={{ top: 0 }}
            />
            <Handle
                type="source"
                position={Position.Bottom}
                className="!w-2 !h-2 !bg-primary"
                style={{ bottom: 0 }}
            />
            <Handle
                type="source"
                position={Position.Left}
                id="left"
                className="!w-2 !h-2 !bg-primary"
                style={{ left: 0 }}
            />
            <Handle
                type="source"
                position={Position.Right}
                id="right"
                className="!w-2 !h-2 !bg-primary"
                style={{ right: 0 }}
            />
        </div>
    );
}

export function TerminalNode({ id, data, selected }: NodeProps) {
    const d = data as unknown as FlowchartNodeData;
    const updateLabel = useUpdateNodeLabel(id);
    const colorStyle = d.color
        ? { backgroundColor: d.color, color: textColorForBg(d.color) }
        : undefined;
    return (
        <div
            className={`px-5 py-2 rounded-full border min-w-[100px] text-center
                ${!d.color ? "bg-card text-card-foreground" : ""}
                ${selected ? "ring-2 ring-primary" : "border-border"}`}
            style={colorStyle}
        >
            <SourceTargetHandles />
            <EditableLabel label={d.label} onChange={updateLabel} />
        </div>
    );
}

export const flowchartNodeTypes = {
    process: ProcessNode,
    decision: DecisionNode,
    terminal: TerminalNode,
};
